import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography
} from "@mui/material";

import Grid from "@mui/material/Grid2";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";

import { AgGridReact } from "ag-grid-react";
import Swal from "sweetalert2";

import { myApi } from "../../config/axiosConfig";

import DefDispensesImportModal from "./modales/DefDispensesImportModal";

const DefCargas = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [importModalOpen, setImportModalOpen] = useState(false);

    const [unidad, setUnidad] = useState("");
    const [division, setDivision] = useState("");
    const [fechaInicial, setFechaInicial] = useState("");
    const [fechaFinal, setFechaFinal] = useState("");

    const columns = useMemo(() => [
        {
            field: "fecha_hora",
            headerName: "FECHA Y HORA",
            minWidth: 180,
            valueFormatter: ({ value }) => {
                if (!value) {
                    return "";
                }

                return new Date(value).toLocaleString("es-MX", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit"
                });
            }
        },
        {
            field: "division",
            headerName: "DIVISIÓN",
            minWidth: 120
        },
        {
            field: "unidad",
            headerName: "UNIDAD",
            minWidth: 120
        },
        {
            field: "producto",
            headerName: "PRODUCTO",
            minWidth: 110
        },
        {
            field: "cantidad_litros",
            headerName: "CANTIDAD (L)",
            minWidth: 140,
            type: "numericColumn",
            valueFormatter: ({ value }) => {
                const number = Number(value);

                if (!Number.isFinite(number)) {
                    return "";
                }

                return number.toLocaleString("es-MX", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        },
        {
            field: "precio_unitario",
            headerName: "PRECIO / L",
            minWidth: 140,
            type: "numericColumn",
            valueFormatter: ({ value }) => {
                if (
                    value === null ||
                    value === undefined ||
                    value === ""
                ) {
                    return "SIN PRECIO";
                }

                const number = Number(value);

                if (!Number.isFinite(number)) {
                    return "SIN PRECIO";
                }

                return number.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4
                });
            }
        },
        {
            field: "monto_total",
            headerName: "MONTO TOTAL",
            minWidth: 160,
            type: "numericColumn",
            valueFormatter: ({ value }) => {
                if (
                    value === null ||
                    value === undefined ||
                    value === ""
                ) {
                    return "SIN PRECIO";
                }

                const number = Number(value);

                if (!Number.isFinite(number)) {
                    return "SIN PRECIO";
                }

                return number.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        },
        {
            field: "dispensador",
            headerName: "DISPENSADOR",
            minWidth: 130
        },
        {
            field: "odometro",
            headerName: "ODÓMETRO",
            minWidth: 130,
            type: "numericColumn",
            valueFormatter: ({ value }) => {
                if (
                    value === null ||
                    value === undefined ||
                    value === ""
                ) {
                    return "";
                }

                const number = Number(value);

                return Number.isFinite(number)
                    ? number.toLocaleString("es-MX", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })
                    : "";
            }
        },
        {
            field: "archivo_origen",
            headerName: "ARCHIVO",
            minWidth: 220
        },
        {
            field: "usuario_alta",
            headerName: "USUARIO",
            minWidth: 190
        }
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: false
    }), []);

    const totalLitros = useMemo(() => {
        return data.reduce((total, row) => {
            return total + (Number(row.cantidad_litros) || 0);
        }, 0);
    }, [data]);

    const totalMonto = useMemo(() => {
        return data.reduce((total, row) => {
            const amount = Number(row.monto_total);

            return Number.isFinite(amount)
                ? total + amount
                : total;
        }, 0);
    }, [data]);

    const precioPromedio = useMemo(() => {
        if (totalLitros <= 0) {
            return null;
        }

        return totalMonto / totalLitros;
    }, [totalMonto, totalLitros]);

    const totalsRow = useMemo(() => ({
        id: "total",
        division: "TOTAL",
        cantidad_litros: totalLitros,
        precio_unitario: precioPromedio,
        monto_total: totalMonto
    }), [
        totalLitros,
        precioPromedio,
        totalMonto
    ]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            const response = await myApi.post(
                "/def/dispenses/search",
                {
                    unidad: unidad.trim() || null,
                    division: division || null,
                    fi: fechaInicial || null,
                    ff: fechaFinal || null
                }
            );

            if (response.data.success) {
                setData(response.data.data || []);
            } else {
                setData([]);

                Swal.fire({
                    icon: "error",
                    title: "No fue posible consultar las cargas",
                    text: response.data.message || ""
                });
            }
        } catch (error) {
            console.error("Error consultando cargas DEF:", error);

            setData([]);

            Swal.fire({
                icon: "error",
                title: "Error al consultar las cargas de DEF"
            });
        } finally {
            setLoading(false);
        }
    }, [
        unidad,
        division,
        fechaInicial,
        fechaFinal
    ]);

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenImport = () => {
        setImportModalOpen(true);
    };

    const handleCloseImport = () => {
        setImportModalOpen(false);
    };

    const handleExport = () => 
    {
        if (data.length === 0) {
            Swal.fire({
                icon: "info",
                title: "No hay información para exportar"
            });

            return;
        }

        const exportData = data.map((row) => ({
            "FECHA Y HORA": row.fecha_hora
                ? new Date(row.fecha_hora)
                : null,
            "DIVISIÓN": row.division || "",
            "UNIDAD": row.unidad || "",
            "PRODUCTO": row.producto || "",
            "CANTIDAD (L)": Number(row.cantidad_litros) || 0,
            "PRECIO / L": row.precio_unitario === null ||
                row.precio_unitario === undefined
                    ? null
                    : Number(row.precio_unitario),

            "MONTO TOTAL": row.monto_total === null ||
                row.monto_total === undefined
                    ? null
                    : Number(row.monto_total),
            "DISPENSADOR": row.dispensador || "",
            "ODÓMETRO":
                row.odometro === null ||
                row.odometro === undefined
                    ? null
                    : Number(row.odometro),
            "ARCHIVO ORIGEN": row.archivo_origen || "",
            "USUARIO ALTA": row.usuario_alta || ""
        }));

        const worksheet = XLSX.utils.json_to_sheet(
            exportData,
            {
                cellDates: true
            }
        );

        const range = XLSX.utils.decode_range(
            worksheet["!ref"]
        );

        for (
            let rowIndex = range.s.r + 1;
            rowIndex <= range.e.r;
            rowIndex += 1
        ) {
            const dateCellAddress = XLSX.utils.encode_cell({
                r: rowIndex,
                c: 0
            });

            if (worksheet[dateCellAddress]) {
                worksheet[dateCellAddress].z =
                    "dd/mm/yyyy hh:mm";
            }
        }

        worksheet["!cols"] = [
            { wch: 20 }, // Fecha
            { wch: 14 }, // División
            { wch: 14 }, // Unidad
            { wch: 12 }, // Producto
            { wch: 16 }, // Cantidad
            { wch: 16 }, // Precio
            { wch: 18 }, // Monto
            { wch: 15 }, // Dispensador
            { wch: 15 }, // Odómetro
            { wch: 35 }, // Archivo
            { wch: 25 }  // Usuario
        ];

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Cargas DEF"
        );

        const today = new Date()
            .toISOString()
            .substring(0, 10);

        XLSX.writeFile(
            workbook,
            `Cargas_DEF_${today}.xlsx`
        );
    };

    return (
        <Paper
            sx={{
                p: 2,
                width: "100%",
                height: "calc(100vh - 120px)", 
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
                overflow: "hidden"
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                >
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Typography
                            variant="h4"
                            fontWeight={700}
                        >
                            Cargas de DEF
                        </Typography>
                    </Grid>

                    <Grid
                        size={{ xs: 12, md: 4 }}
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: {
                                xs: "flex-start",
                                md: "flex-end"
                            }
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            onClick={handleOpenImport}
                            sx={{
                                backgroundColor: "black"
                            }}
                        >
                            Subir Excel
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleExport}
                        >
                            Exportar Excel
                        </Button>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                            fullWidth
                            label="Unidad"
                            value={unidad}
                            onChange={(event) => {
                                setUnidad(event.target.value);
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>División</InputLabel>

                            <Select
                                label="División"
                                value={division}
                                onChange={(event) => {
                                    setDivision(event.target.value);
                                }}
                            >
                                <MenuItem value="">
                                    TODAS
                                </MenuItem>

                                <MenuItem value="EBT">
                                    EBT
                                </MenuItem>

                                <MenuItem value="TEDEL">
                                    TEDEL
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Fecha inicial"
                            value={fechaInicial}
                            onChange={(event) => {
                                setFechaInicial(event.target.value);
                            }}
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Fecha final"
                            value={fechaFinal}
                            onChange={(event) => {
                                setFechaFinal(event.target.value);
                            }}
                            slotProps={{
                                inputLabel: {
                                    shrink: true
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<ManageSearchIcon />}
                            onClick={fetchData}
                            disabled={loading}
                            sx={{
                                height: 56,
                                color: "black",
                                backgroundColor: "#d5d8dc",
                                "&:hover": {
                                    backgroundColor: "#bfc5c9"
                                }
                            }}
                        >
                            {loading ? "Buscando..." : "Buscar"}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box
                sx={{
                    width: "100%",
                    height: "calc(100vh - 220px)",
                    mt: 2
                }}
            >
                <div
                    className="ag-theme-quartz"
                    style={{
                        width: "100%",
                        height: "100%"
                    }}
                >
                    <AgGridReact
                        rowData={data}
                        columnDefs={columns}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={100}
                        pinnedBottomRowData={[totalsRow]}
                        getRowId={({ data: row }) => String(row.id)}
                        overlayNoRowsTemplate="No hay cargas de DEF"
                    />
                </div>
            </Box>

            <DefDispensesImportModal
                open={importModalOpen}
                onClose={handleCloseImport}
                onImported={fetchData}
            />
        </Paper>
    );
};

export default DefCargas;